import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  IconButton,
  Tooltip,
  Fade,
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  Vaccines as VaccinesIcon,
  PhotoCamera as PhotoCameraIcon,
  LocalHospital as HospitalIcon,
  Description as DescriptionIcon,
  Psychology as PsychologyIcon,
  TrendingUp as TrendingUpIcon,
  ChildCare as ChildCareIcon,
  Schedule as ScheduleIcon,
  ArrowForward as ArrowForwardIcon,
  Link as LinkIcon,
} from '@mui/icons-material';

interface QuickLink {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  category: string;
  priority: 'high' | 'medium' | 'low';
  relatedTo: string[];
}

interface QuickLinksProps {
  currentSection: string;
  onNavigate: (path: string) => void;
  maxLinks?: number;
  showCategory?: boolean;
}

const QuickLinks: React.FC<QuickLinksProps> = ({
  currentSection,
  onNavigate,
  maxLinks = 6,
  showCategory = true,
}) => {
  const allLinks: QuickLink[] = [
    {
      id: 'calculator',
      title: 'Калькулятор дозировок',
      description: 'Расчет дозировок лекарств',
      icon: <CalculateIcon />,
      path: '/calculator',
      category: 'Инструменты',
      priority: 'high',
      relatedTo: ['children-up-to-year', 'emergency-care', 'ai-assistant']
    },
    {
      id: 'vaccination',
      title: 'Календарь вакцинации',
      description: 'Национальный календарь прививок',
      icon: <VaccinesIcon />,
      path: '/vaccination',
      category: 'Профилактика',
      priority: 'high',
      relatedTo: ['children-up-to-year', 'orders', 'growth-charts']
    },
    {
      id: 'atlas',
      title: 'Атлас сыпей',
      description: 'Диагностика кожных высыпаний',
      icon: <PhotoCameraIcon />,
      path: '/atlas',
      category: 'Диагностика',
      priority: 'high',
      relatedTo: ['emergency-care', 'children-up-to-year', 'ai-assistant']
    },
    {
      id: 'emergency-care',
      title: 'Неотложная помощь',
      description: 'Алгоритмы экстренной помощи',
      icon: <HospitalIcon />,
      path: '/emergency-care',
      category: 'Экстренная помощь',
      priority: 'high',
      relatedTo: ['calculator', 'atlas', 'ai-assistant']
    },
    {
      id: 'children-up-to-year',
      title: 'Дети до года',
      description: 'Развитие и уход за младенцами',
      icon: <ChildCareIcon />,
      path: '/children-up-to-year',
      category: 'Развитие',
      priority: 'high',
      relatedTo: ['calculator', 'vaccination', 'atlas', 'growth-charts']
    },
    {
      id: 'growth-charts',
      title: 'Графики роста',
      description: 'Нормы роста и веса по ВОЗ',
      icon: <TrendingUpIcon />,
      path: '/growth-charts',
      category: 'Развитие',
      priority: 'medium',
      relatedTo: ['children-up-to-year', 'calculator']
    },
    {
      id: 'orders',
      title: 'Приказы и нормативы',
      description: 'Профилактические осмотры',
      icon: <DescriptionIcon />,
      path: '/orders',
      category: 'Нормативы',
      priority: 'medium',
      relatedTo: ['vaccination', 'children-up-to-year']
    },
    {
      id: 'ai-assistant',
      title: 'ИИ-помощник',
      description: 'Анализ медицинских данных',
      icon: <PsychologyIcon />,
      path: '/ai-assistant',
      category: 'Инструменты',
      priority: 'medium',
      relatedTo: ['calculator', 'emergency-care', 'atlas']
    }
  ];

  // Получаем связанные разделы для текущего раздела
  const getRelatedLinks = (): QuickLink[] => {
    const currentLink = allLinks.find(link => link.id === currentSection);
    if (!currentLink) return [];

    const relatedIds = currentLink.relatedTo;
    const relatedLinks = allLinks
      .filter(link => relatedIds.includes(link.id))
      .sort((a, b) => {
        // Сначала по приоритету, потом по алфавиту
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        return priorityOrder[a.priority] - priorityOrder[b.priority] || 
               a.title.localeCompare(b.title);
      })
      .slice(0, maxLinks);

    return relatedLinks;
  };

  const relatedLinks = getRelatedLinks();

  if (relatedLinks.length === 0) {
    return null;
  }

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'Инструменты': '#007bff',
      'Профилактика': '#28a745',
      'Диагностика': '#ffc107',
      'Экстренная помощь': '#dc3545',
      'Развитие': '#17a2b8',
      'Нормативы': '#6f42c1',
    };
    return colors[category] || '#6c757d';
  };

  const getPriorityColor = (priority: string) => {
    const colors: { [key: string]: string } = {
      high: '#dc3545',
      medium: '#ffc107',
      low: '#28a745',
    };
    return colors[priority] || '#6c757d';
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <LinkIcon sx={{ mr: 1, color: '#007bff' }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#495057' }}>
          Связанные разделы
        </Typography>
      </Box>

      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { 
          xs: '1fr', 
          sm: 'repeat(2, 1fr)', 
          md: 'repeat(3, 1fr)' 
        }, 
        gap: 2 
      }}>
        {relatedLinks.map((link, index) => (
          <Fade in={true} timeout={300 + index * 100} key={link.id}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 2,
                border: '1px solid #e9ecef',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  borderColor: '#007bff',
                },
              }}
            >
              <CardActionArea
                onClick={() => onNavigate(link.path)}
                sx={{ height: '100%', p: 0 }}
              >
                <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      color: getCategoryColor(link.category),
                      mb: 1 
                    }}>
                      {link.icon}
                      <Typography variant="h6" sx={{ ml: 1, fontWeight: 600, fontSize: '1rem' }}>
                        {link.title}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Chip
                        label={link.priority === 'high' ? 'Важно' : link.priority === 'medium' ? 'Средне' : 'Доп.'}
                        size="small"
                        sx={{
                          fontSize: '0.7rem',
                          height: 20,
                          backgroundColor: getPriorityColor(link.priority),
                          color: 'white',
                        }}
                      />
                      <ArrowForwardIcon sx={{ fontSize: 16, color: '#6c757d' }} />
                    </Box>
                  </Box>

                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 1.5, 
                      flexGrow: 1,
                      lineHeight: 1.4 
                    }}
                  >
                    {link.description}
                  </Typography>

                  {showCategory && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Chip
                        label={link.category}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: '0.7rem',
                          height: 20,
                          borderColor: getCategoryColor(link.category),
                          color: getCategoryColor(link.category),
                        }}
                      />
                      <Tooltip title="Перейти к разделу">
                        <IconButton size="small" sx={{ p: 0.5 }}>
                          <ArrowForwardIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Fade>
        ))}
      </Box>
    </Box>
  );
};

export default QuickLinks;
